import { Form, FormField, FormItem, FormLabel } from '@/components/ui-kit/form';
import { Slider } from '@/components/ui-kit/slider';
import { InfoTooltip } from '@/components/ui-kit/info-tool-tip';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AgentBehaviorSettingsForm } from '@/modules/gpt-chats/components/agents-configuration/components/config-card-section/utils';
import { SliderControl } from '../slider-control';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import { useEffect, useState } from 'react';

type KnowledgeRecallModalProps = {
  form: UseFormReturn<AgentBehaviorSettingsForm>;
  onClose: () => void;
  isMobile?: boolean;
};

export const KnowledgeRecallModal = ({
  form,
  onClose,
  isMobile = false,
}: KnowledgeRecallModalProps) => {
  const { t } = useTranslation();
  const denseWeightValue = form.watch('rag_config.dense_weight') ?? 0.9;

  const semanticPercentage = Math.round(denseWeightValue * 100);

  const searchType = denseWeightValue === 1.0 ? 'semantic' : 'mixed';

  const [localValue, setLocalValue] = useState(semanticPercentage);

  useEffect(() => {
    setLocalValue(semanticPercentage);
  }, [semanticPercentage]);

  return (
    <>
      <div className="fixed inset-0 z-[100]" onClick={onClose} />

      <div
        className="fixed z-[101] flex max-h-[35vh] flex-col overflow-hidden rounded-lg border bg-card shadow-lg"
        style={(() => {
          const element = document.getElementById('knowledge-recall-buttons');
          if (element) {
            const rect = element.getBoundingClientRect();
            return {
              left: `${rect.left}px`,
              top: `${rect.bottom + 8}px`,
              width: `${rect.width}px`,
            };
          }
          return {};
        })()}
      >
        <div
          className="flex-1 overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgb(156 163 175) transparent',
          }}
        >
          <div className="p-6">
            <h3 className="pb-6 text-lg font-semibold">{t('KNOWLEDGE_RECALL')}</h3>

            <Form {...form}>
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="rag_config.score_threshold"
                  render={({ field }) => (
                    <FormItem>
                      <SliderControl
                        label={t('KNOWLEDGE_RELEVANCE')}
                        tooltipContent={t('KNOWLEDGE_RELEVANCE_TOOLTIP')}
                        value={field.value || 0.7}
                        onChange={field.onChange}
                        min={0}
                        max={0.95}
                        step={0.01}
                        isMobile={isMobile}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rag_config.top_k"
                  render={({ field }) => (
                    <FormItem>
                      <SliderControl
                        label={t('RECALL_NUMBER')}
                        tooltipContent={t('RECALL_NUMBER_TOOLTIP')}
                        value={field.value || 10}
                        onChange={field.onChange}
                        min={1}
                        max={10}
                        step={1}
                        isMobile={isMobile}
                      />
                    </FormItem>
                  )}
                />

                <div>
                  <FormLabel className="flex items-center gap-2 text-base font-medium">
                    {t('SEARCH_WEIGHT')}
                    <InfoTooltip content={t('SEARCH_WEIGHT_TOOLTIP')} />
                  </FormLabel>

                  <div className="mt-4 space-y-4">
                    <RadioGroup
                      value={searchType}
                      onValueChange={(value) => {
                        if (value === 'semantic') {
                          form.setValue('rag_config.dense_weight', 1.0, { shouldDirty: false });
                          form.setValue('rag_config.keyword_weight', 0.0, { shouldDirty: false });
                        } else {
                          form.setValue('rag_config.dense_weight', 0.9, { shouldDirty: false });
                          form.setValue('rag_config.keyword_weight', 0.1, { shouldDirty: false });
                        }
                      }}
                      className="flex gap-8"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="mixed" id="search-mixed" />
                        <label
                          htmlFor="search-mixed"
                          className="cursor-pointer text-sm font-medium"
                        >
                          {t('MIXED_SEARCH')}
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="semantic" id="search-semantic" />
                        <label
                          htmlFor="search-semantic"
                          className="cursor-pointer text-sm font-medium"
                        >
                          {t('SEMANTIC_SEARCH')}
                        </label>
                      </div>
                    </RadioGroup>

                    {searchType === 'mixed' && (
                      <div className="space-y-3">
                        <div className="relative flex h-6 items-center">
                          <div className="pointer-events-none absolute top-1/2 h-1.5 w-full -translate-y-1/2 overflow-hidden rounded-full">
                            <div className="flex h-full">
                              <div
                                className="h-full transition-all duration-75 ease-out"
                                style={{
                                  width: `${localValue}%`,
                                  backgroundColor: '#124091',
                                }}
                              />
                              <div
                                className="h-full transition-all duration-75 ease-out"
                                style={{
                                  width: `${100 - localValue}%`,
                                  backgroundColor: '#06b6d4',
                                }}
                              />
                            </div>
                          </div>

                          <div className="relative z-10 w-full">
                            <Slider
                              min={70}
                              max={90}
                              step={1}
                              value={[localValue]}
                              onValueChange={(value) => {
                                const semanticPercent = value[0];
                                const keywordPercent = 100 - semanticPercent;

                                setLocalValue(semanticPercent);

                                form.setValue('rag_config.dense_weight', semanticPercent / 100, {
                                  shouldValidate: false,
                                  shouldDirty: false,
                                });
                                form.setValue('rag_config.keyword_weight', keywordPercent / 100, {
                                  shouldValidate: false,
                                  shouldDirty: false,
                                });
                              }}
                              className="[&_[data-slot=slider-range]]:bg-transparent [&_[data-slot=slider-thumb]]:hidden [&_[data-slot=slider-track]]:h-1.5 [&_[data-slot=slider-track]]:bg-transparent"
                            />
                            <div
                              className="pointer-events-none absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[1px] border-primary bg-white shadow-md transition-all duration-75 ease-out"
                              style={{
                                left: `${localValue}%`,
                              }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <div className="text-medium-emphasis">
                            {t('SEMANTIC')}:{' '}
                            <span className="font-semibold" style={{ color: '#1e40af' }}>
                              {localValue}%
                            </span>
                          </div>
                          <div className="text-medium-emphasis">
                            {t('KEYWORDS')}:{' '}
                            <span className="font-semibold text-cyan-500">{100 - localValue}%</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};
